import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { FiArrowLeft, FiArrowRight, FiCheck, FiPlus, FiX, FiExternalLink, FiAlertCircle, FiCheckCircle } from 'react-icons/fi';
import api from '../../utils/api';
import { trackCompleteRegistration } from '../../utils/metaPixel';
import './RegistrationStepsPage.css';

// Social media validation helpers
const socialMediaHelpers = {
  instagram: {
    label: 'Instagram',
    placeholder: 'שם משתמש או קישור לפרופיל',
    example: 'לדוגמה: username או @username',
    isUrl: (input) => /^https?:\/\/(www\.)?instagram\.com\//i.test(input),
    extractUsername: (input) => {
      if (!input) return '';
      const trimmed = input.trim();
      if (/^https?:\/\//i.test(trimmed)) {
        const match = trimmed.match(/instagram\.com\/([a-zA-Z0-9._]+)/i);
        return match ? match[1] : '';
      }
      return trimmed.replace(/^@/, '');
    },
    isValidUsername: (username) => {
      if (!username) return false;
      return /^[a-zA-Z0-9._]{1,30}$/.test(username);
    },
    buildUrl: (username) => username ? `https://www.instagram.com/${username}/` : null
  },
  tiktok: {
    label: 'TikTok',
    placeholder: 'שם משתמש או קישור לפרופיל',
    example: 'לדוגמה: username או @username',
    isUrl: (input) => /^https?:\/\/(www\.)?tiktok\.com\//i.test(input),
    extractUsername: (input) => {
      if (!input) return '';
      const trimmed = input.trim();
      if (/^https?:\/\//i.test(trimmed)) {
        const match = trimmed.match(/tiktok\.com\/@?([a-zA-Z0-9._]+)/i);
        return match ? match[1] : '';
      }
      return trimmed.replace(/^@/, '');
    },
    isValidUsername: (username) => {
      if (!username) return false;
      return /^[a-zA-Z0-9._]{2,24}$/.test(username);
    },
    buildUrl: (username) => username ? `https://www.tiktok.com/@${username}` : null
  },
  facebook: {
    label: 'Facebook',
    placeholder: 'קישור לפרופיל או לדף',
    example: 'לדוגמה: שם הדף או שם המשתמש',
    isUrl: (input) => /^https?:\/\/(www\.)?facebook\.com\//i.test(input),
    extractUsername: (input) => {
      if (!input) return '';
      const trimmed = input.trim();
      if (/^https?:\/\//i.test(trimmed)) {
        const match = trimmed.match(/facebook\.com\/([a-zA-Z0-9._-]+)/i);
        return match ? match[1] : trimmed;
      }
      return trimmed;
    },
    isValidUsername: (username) => {
      if (!username) return false;
      // Facebook usernames/page names
      return username.length >= 1 && !/^(share|profile\.php|groups|events|watch)$/i.test(username);
    },
    buildUrl: (username) => username ? `https://www.facebook.com/${username}` : null
  }
};

const RegistrationStepsPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(2);
  const [isLoading, setIsLoading] = useState(false);
  const [customCategory, setCustomCategory] = useState('');
  const [pendingRegistration, setPendingRegistration] = useState(null);
  const [socialValidation, setSocialValidation] = useState({
    instagram: { isValid: null, username: '' },
    tiktok: { isValid: null, username: '' },
    facebook: { isValid: null, username: '' }
  });
  const [profileChecked, setProfileChecked] = useState({
    instagram: false,
    tiktok: false,
    facebook: false
  });

  // Load pending registration from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('pendingCreatorRegistration');
    if (!stored) {
      navigate('/register', { replace: true });
      return;
    }
    setPendingRegistration(JSON.parse(stored));
  }, [navigate]);

  const [formData, setFormData] = useState({
    gender: '',
    birthYear: '',
    phone: '',
    city: '',
    customCity: false,
    preferredPlatforms: [],
    categories: [],
    customCategories: [],
    socialMedia: {
      instagram: { username: '' },
      tiktok: { username: '' },
      facebook: { username: '' }
    }
  });

  const totalSteps = 6;

  // Handle social media input change with validation
  const handleSocialMediaChange = (platform, value) => {
    const helper = socialMediaHelpers[platform];

    // Facebook allows Hebrew and spaces, other platforms don't
    const isFacebook = platform === 'facebook';

    // Check for Hebrew characters - show error instead of filtering (except Facebook)
    const hasHebrew = !isFacebook && /[\u0590-\u05FF]/.test(value);

    // Check for spaces in non-URL input (except Facebook)
    const isUrl = /^https?:\/\//i.test(value);
    const hasInvalidSpaces = !isFacebook && !isUrl && /\s/.test(value);

    let isValid = null;
    let extractedUsername = '';

    if (value.trim()) {
      if (hasHebrew || hasInvalidSpaces) {
        // Mark as invalid if contains Hebrew or spaces (not for Facebook)
        isValid = false;
        extractedUsername = '';
      } else {
        extractedUsername = helper.extractUsername(value);
        isValid = helper.isValidUsername(extractedUsername);
      }
    }

    setSocialValidation(prev => ({
      ...prev,
      [platform]: { isValid, username: extractedUsername, hasHebrew, hasInvalidSpaces }
    }));

    setFormData(prev => ({
      ...prev,
      socialMedia: {
        ...prev.socialMedia,
        [platform]: { username: value }
      }
    }));

    // Any edit invalidates the previous "profile checked" confirmation
    setProfileChecked(prev => ({ ...prev, [platform]: false }));
  };

  // When the user leaves the input, snap the visible value to the clean canonical form
  const handleSocialMediaBlur = (platform) => {
    const validation = socialValidation[platform];
    if (!validation?.isValid || !validation.username) return;

    const isFacebook = platform === 'facebook';
    const display = isFacebook ? validation.username : `@${validation.username}`;

    setFormData(prev => {
      if (prev.socialMedia[platform]?.username === display) return prev;
      return {
        ...prev,
        socialMedia: {
          ...prev.socialMedia,
          [platform]: { username: display }
        }
      };
    });
  };

  // User clicked "\u05D1\u05D3\u05D5\u05E7 \u05E4\u05E8\u05D5\u05E4\u05D9\u05DC" \u2014 record that they opened the profile so we can proceed
  const handleProfileChecked = (platform) => {
    setProfileChecked(prev => ({ ...prev, [platform]: true }));
  };

  // Build profile URLs based on validated username
  const getProfileUrl = (platform) => {
    const validation = socialValidation[platform];
    if (validation.isValid && validation.username) {
      return socialMediaHelpers[platform].buildUrl(validation.username);
    }
    return null;
  };

  const genderOptions = [
    { value: 'female', label: 'נקבה' },
    { value: 'male', label: 'זכר' },
    { value: 'other', label: 'אחר' },
    { value: 'prefer_not_to_say', label: 'מעדיף/ה לא לציין' }
  ];

  const platformOptions = [
    { value: 'instagram', label: 'אינסטגרם' },
    { value: 'tiktok', label: 'טיקטוק' },
    { value: 'facebook', label: 'פייסבוק' }
  ];

  const categoryOptions = [
    { value: 'fashion', label: 'אופנה' },
    { value: 'beauty', label: 'יופי' },
    { value: 'lifestyle', label: 'לייפסטייל' },
    { value: 'food', label: 'אוכל' },
    { value: 'travel', label: 'טיולים' },
    { value: 'fitness', label: 'כושר' },
    { value: 'tech', label: 'טכנולוגיה' },
    { value: 'gaming', label: 'גיימינג' },
    { value: 'parenting', label: 'הורות' },
    { value: 'business', label: 'עסקים' },
    { value: 'art', label: 'אמנות' },
    { value: 'music', label: 'מוזיקה' },
    { value: 'comedy', label: 'קומדיה' },
    { value: 'education', label: 'חינוך' },
    { value: 'pets', label: 'חיות מחמד' }
  ];


  const cities = [
    'תל אביב', 'ירושלים', 'חיפה', 'באר שבע', 'ראשון לציון',
    'פתח תקווה', 'נתניה', 'אשדוד', 'בני ברק', 'חולון',
    'רמת גן', 'אשקלון', 'הרצליה', 'כפר סבא', 'רעננה'
  ];

  const handleMultiSelect = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value]
    }));
  };

  const handleAddCustomCategory = () => {
    const trimmed = customCategory.trim();
    if (trimmed && !formData.customCategories.includes(trimmed)) {
      setFormData(prev => ({
        ...prev,
        customCategories: [...prev.customCategories, trimmed]
      }));
      setCustomCategory('');
    }
  };

  const handleRemoveCustomCategory = (category) => {
    setFormData(prev => ({
      ...prev,
      customCategories: prev.customCategories.filter(c => c !== category)
    }));
  };

  const handleNext = async () => {
    // Validate required fields based on current step
    if (currentStep === 2) {
      if (!formData.gender) {
        toast.error('יש לבחור מגדר');
        return;
      }
      if (!formData.birthYear) {
        toast.error('יש לבחור שנת לידה');
        return;
      }
      if (!formData.phone.trim()) {
        toast.error('יש להזין מספר טלפון');
        return;
      }
      // Validate phone has exactly 10 digits
      const phoneDigits = formData.phone.replace(/\D/g, '');
      if (phoneDigits.length !== 10) {
        toast.error('מספר טלפון חייב להכיל 10 ספרות');
        return;
      }
    }

    if (currentStep === 3) {
      if (!formData.city.trim()) {
        toast.error('יש לבחור או להזין עיר מגורים');
        return;
      }
    }

    if (currentStep === 4) {
      if (formData.preferredPlatforms.length === 0) {
        toast.error('יש לבחור לפחות פלטפורמה אחת');
        return;
      }
    }

    if (currentStep === 5) {
      if (formData.categories.length === 0 && formData.customCategories.length === 0) {
        toast.error('יש לבחור לפחות קטגוריה אחת');
        return;
      }
    }

    if (currentStep === 6) {
      // Check if at least one social media is filled
      const hasAnySocial = formData.preferredPlatforms.some(
        platform => formData.socialMedia[platform]?.username.trim()
      );

      if (!hasAnySocial) {
        toast.error('יש להזין לפחות רשת חברתית אחת');
        return;
      }

      // Validate each filled platform
      for (const platform of formData.preferredPlatforms) {
        const input = formData.socialMedia[platform]?.username.trim();
        if (input && !socialValidation[platform]?.isValid) {
          const platformLabel = socialMediaHelpers[platform]?.label || platform;
          toast.error(`הקישור או שם המשתמש ב-${platformLabel} אינו תקין`);
          return;
        }
        // Require the user to actually open and confirm the profile
        if (input && socialValidation[platform]?.isValid && !profileChecked[platform]) {
          const platformLabel = socialMediaHelpers[platform]?.label || platform;
          toast.error(`יש ללחוץ על "בדוק פרופיל" עבור ${platformLabel} ולוודא שהפרופיל קיים`);
          return;
        }
      }
    }

    if (currentStep < totalSteps) {
      // Not final step — just advance locally
      setCurrentStep(currentStep + 1);
      return;
    }

    // Final step — submit everything to register-complete
    await submitRegistration(pendingRegistration.email);
  };

  // Separate function for submitting registration (can be called with different email)
  const submitRegistration = async (emailToUse) => {
    setIsLoading(true);
    try {
      // Use the validated/extracted usernames
      const igUsername = socialValidation.instagram.username || '';
      const ttUsername = socialValidation.tiktok.username || '';
      const fbUsername = socialValidation.facebook.username || '';

      await api.post('/auth/register-complete', {
        email: emailToUse,
        password: pendingRegistration.password,
        fullName: pendingRegistration.fullName,
        displayName: pendingRegistration.displayName,
        gender: formData.gender,
        birthYear: formData.birthYear,
        phone: formData.phone,
        city: formData.city,
        preferredPlatforms: formData.preferredPlatforms,
        categories: formData.categories,
        customCategories: formData.customCategories,
        instagramUsername: igUsername || null,
        tiktokUsername: ttUsername || null,
        facebookUsername: fbUsername || null
      });

      trackCompleteRegistration({ content_name: 'creator_registration' });

      // Clear pending data and redirect
      localStorage.removeItem('pendingCreatorRegistration');
      navigate('/register/complete');
    } catch (error) {
      const message = error.response?.data?.error || 'שגיאה ביצירת המשתמש';

      // Check if error is about email already registered
      if (message.includes('אימייל') || message.includes('email') || message.includes('רשומ')) {
        // Show popup to update email
        const { value: newEmail } = await Swal.fire({
          title: 'כתובת האימייל כבר רשומה',
          text: 'אנא הזן כתובת אימייל חדשה:',
          input: 'email',
          inputValue: emailToUse,
          inputPlaceholder: 'הזן אימייל חדש',
          showCancelButton: true,
          confirmButtonText: 'עדכן והמשך',
          cancelButtonText: 'ביטול',
          confirmButtonColor: '#F7941D',
          inputValidator: (value) => {
            if (!value) {
              return 'יש להזין כתובת אימייל';
            }
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
              return 'כתובת אימייל לא תקינה';
            }
            if (value === emailToUse) {
              return 'יש להזין כתובת אימייל שונה';
            }
          }
        });

        if (newEmail) {
          // Update pendingRegistration with new email
          const updatedRegistration = { ...pendingRegistration, email: newEmail };
          setPendingRegistration(updatedRegistration);
          localStorage.setItem('pendingCreatorRegistration', JSON.stringify(updatedRegistration));

          // Retry registration with new email directly (not through handleNext)
          toast.info('מנסה שוב עם האימייל החדש...');
          setIsLoading(false); // Reset loading before retry
          await submitRegistration(newEmail);
          return; // Exit to avoid finally block setting loading to false twice
        }
      } else {
        toast.error(message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 2) {
      setCurrentStep(currentStep - 1);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 2:
        return (
          <div className="step-content">
            <h2>ספר/י לנו קצת על עצמך</h2>
            <p>מידע זה עוזר לנו להתאים לך קמפיינים רלוונטיים</p>

            <div className="form-group">
              <label className="form-label">מגדר <span className="required">*</span></label>
              <div className="options-grid">
                {genderOptions.map(opt => (
                  <label
                    key={opt.value}
                    className={`option-card ${formData.gender === opt.value ? 'selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="gender"
                      value={opt.value}
                      checked={formData.gender === opt.value}
                      onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                    />
                    <span>{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">שנת לידה <span className="required">*</span></label>
              <select
                className="form-select"
                value={formData.birthYear}
                onChange={(e) => setFormData({ ...formData, birthYear: e.target.value })}
              >
                <option value="">בחר שנה</option>
                {Array.from({ length: 50 }, (_, i) => new Date().getFullYear() - 16 - i).map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">טלפון <span className="required">*</span></label>
              <input
                type="tel"
                className="form-input"
                placeholder="0501234567"
                value={formData.phone}
                onChange={(e) => {
                  // Only allow digits
                  const value = e.target.value.replace(/\D/g, '');
                  setFormData({ ...formData, phone: value });
                }}
                dir="ltr"
                maxLength="10"
              />
              <span className="helper-text">10 ספרות בלבד</span>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="step-content">
            <h2>איפה את/ה גר/ה?</h2>
            <p>מותגים מחפשים יוצרים מאזורים שונים בארץ</p>

            <div className="form-group">
              <label className="form-label">עיר מגורים <span className="required">*</span></label>
              <select
                className="form-select"
                value={cities.includes(formData.city) ? formData.city : (formData.city ? 'other' : '')}
                onChange={(e) => {
                  if (e.target.value === 'other') {
                    setFormData({ ...formData, city: '', customCity: true });
                  } else {
                    setFormData({ ...formData, city: e.target.value, customCity: false });
                  }
                }}
              >
                <option value="">בחר עיר</option>
                {cities.map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
                <option value="other">אחר</option>
              </select>
            </div>

            {(formData.customCity || (!cities.includes(formData.city) && formData.city)) && (
              <div className="form-group">
                <label className="form-label">הזן שם עיר</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="שם העיר"
                  value={formData.city === 'other' ? '' : formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  required
                />
              </div>
            )}
          </div>
        );

      case 4:
        return (
          <div className="step-content">
            <h2>באילו פלטפורמות את/ה פעיל/ה? <span className="required">*</span></h2>
            <p>בחר לפחות פלטפורמה אחת שבה יש לך נוכחות</p>

            <div className="options-grid cols-2">
              {platformOptions.map(opt => (
                <label
                  key={opt.value}
                  className={`option-card ${formData.preferredPlatforms.includes(opt.value) ? 'selected' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={formData.preferredPlatforms.includes(opt.value)}
                    onChange={() => handleMultiSelect('preferredPlatforms', opt.value)}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
        );

      case 5:
        return (
          <div className="step-content">
            <h2>מה הנושאים שלך? <span className="required">*</span></h2>
            <p>בחר לפחות קטגוריה אחת שמתאימה לתוכן שלך</p>

            <div className="options-grid cols-3">
              {categoryOptions.map(opt => (
                <label
                  key={opt.value}
                  className={`option-card small ${formData.categories.includes(opt.value) ? 'selected' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={formData.categories.includes(opt.value)}
                    onChange={() => handleMultiSelect('categories', opt.value)}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>

            {/* Custom Categories */}
            <div className="custom-category-section">
              <label className="form-label">הוסף קטגוריה מותאמת אישית</label>
              <div className="custom-category-input">
                <input
                  type="text"
                  className="form-input"
                  placeholder="לדוגמה: ספורט אתגרי"
                  value={customCategory}
                  onChange={(e) => setCustomCategory(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCustomCategory())}
                />
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleAddCustomCategory}
                  disabled={!customCategory.trim()}
                >
                  <FiPlus />
                </button>
              </div>

              {formData.customCategories.length > 0 && (
                <div className="custom-categories-list">
                  {formData.customCategories.map(cat => (
                    <span key={cat} className="custom-category-tag">
                      {cat}
                      <button
                        type="button"
                        onClick={() => handleRemoveCustomCategory(cat)}
                        className="remove-tag-btn"
                      >
                        <FiX />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      case 6:
        return (
          <div className="step-content">
            <h2>מה הרשתות החברתיות שלך? <span className="required">*</span></h2>
            <p>יש להזין לפחות רשת חברתית אחת - שם משתמש או קישור לפרופיל</p>

            <div className="important-notice blink">
              <FiAlertCircle className="notice-icon" />
              <div className="notice-text">
                <strong>שימו לב!</strong> לאחר הזנת שם משתמש או קישור, יש ללחוץ על כפתור "בדוק פרופיל" כדי לוודא שהפרופיל תקין ונגיש.
                <div className="notice-highlight">פרופיל שלא יהיה תקין או נגיש - לא יאושר במערכת.</div>
              </div>
            </div>

            {formData.preferredPlatforms.map(platform => {
              const helper = socialMediaHelpers[platform];
              if (!helper) return null;

              const validation = socialValidation[platform];
              const value = formData.socialMedia[platform]?.username || '';

              return (
                <div className="form-group" key={platform}>
                  <label className="form-label">{helper.label}</label>
                  <div className="social-input-wrapper">
                    <input
                      type="text"
                      className={`form-input ${validation?.isValid === false ? 'input-error' : ''} ${validation?.isValid === true ? 'input-success' : ''}`}
                      placeholder={helper.placeholder}
                      value={value}
                      onChange={(e) => handleSocialMediaChange(platform, e.target.value)}
                      onBlur={() => handleSocialMediaBlur(platform)}
                      dir="ltr"
                    />
                    {validation?.isValid === true && (
                      <span className="validation-icon valid"><FiCheckCircle /></span>
                    )}
                    {validation?.isValid === false && (
                      <span className="validation-icon invalid"><FiAlertCircle /></span>
                    )}
                    {getProfileUrl(platform) && (
                      <a
                        href={getProfileUrl(platform)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`verify-profile-btn ${profileChecked[platform] ? 'checked' : ''}`}
                        title="בדוק שהפרופיל קיים"
                        onClick={() => handleProfileChecked(platform)}
                      >
                        {profileChecked[platform] ? <FiCheckCircle /> : <FiExternalLink />}
                        {profileChecked[platform] ? 'פרופיל אומת' : 'בדוק פרופיל'}
                      </a>
                    )}
                  </div>
                  {validation?.isValid === true && validation?.username && (
                    <span className="helper-text success">
                      {platform === 'facebook' ? 'פרופיל' : 'שם משתמש'}: {platform !== 'facebook' ? '@' : ''}{validation.username}
                    </span>
                  )}
                  {validation?.isValid === true && !profileChecked[platform] && (
                    <span className="helper-text warning">
                      יש ללחוץ על "בדוק פרופיל" כדי לוודא שהפרופיל קיים לפני המשך
                    </span>
                  )}
                  {validation?.isValid === false && (
                    <span className="helper-text error">
                      {validation?.hasHebrew
                        ? 'יש להזין באנגלית בלבד'
                        : validation?.hasInvalidSpaces
                          ? 'אין להזין רווחים'
                          : 'הקישור או שם המשתמש אינו תקין'}
                    </span>
                  )}
                  {validation?.isValid === null && !value && (
                    <span className="helper-text">{helper.example}</span>
                  )}
                </div>
              );
            })}
          </div>
        );

      default:
        return null;
    }
  };

  if (!pendingRegistration) {
    return null;
  }

  return (
    <div className="registration-steps-page">
      <div className="steps-container">
        {/* Progress Bar */}
        <div className="progress-bar">
          {Array.from({ length: totalSteps - 1 }, (_, i) => i + 2).map(step => (
            <div
              key={step}
              className={`progress-step ${step <= currentStep ? 'active' : ''} ${step < currentStep ? 'completed' : ''}`}
            >
              {step < currentStep ? <FiCheck /> : step - 1}
            </div>
          ))}
        </div>

        {/* Step Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="steps-navigation">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={handleBack}
            disabled={currentStep <= 2}
          >
            <FiArrowRight />
            הקודם
          </button>

          <button
            type="button"
            className="btn btn-primary"
            onClick={handleNext}
            disabled={isLoading}
          >
            {isLoading ? 'שומר...' : currentStep === totalSteps ? 'סיום' : 'הבא'}
            {currentStep < totalSteps && <FiArrowLeft />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RegistrationStepsPage;
